import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import { getProposals, fetchProposalPdf, resendProposal, fetchProposalDocument } from "../lib/api";
import { formatCurrency, ELECTION_TYPES, GOVERNING_REGULATIONS } from "../lib/costEngine";
import { fmtDateTime } from "../lib/proposalPdf";
import { ClientActivityLog } from "../components/admin/ClientActivityLog";
import { SendEmailDialog } from "../components/admin/SendEmailDialog";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ArrowLeft,
  Users,
  Mail,
  Phone,
  Building2,
  Briefcase,
  MapPin,
  Eye,
  Download,
  Loader2,
  Inbox,
  Send,
  FileText,
} from "lucide-react";
import { toast } from "sonner";

export default function ClientPage() {
  const { email: emailParam } = useParams();
  const email = decodeURIComponent(emailParam || "").toLowerCase();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const rows = await getProposals(email);
      setProposals(rows);
    } catch (e) {
      toast.error("Could not load client.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the email param changes
  }, [email]);

  // Most recent record holds the freshest details.
  const client = useMemo(() => (proposals[0] ? proposals[0].lead : null), [proposals]);
  const totalValue = useMemo(
    () => proposals.reduce((s, p) => s + (Number(p.total) || 0), 0),
    [proposals]
  );

  const openPdf = async (p, mode) => {
    setBusyId(p.id + mode);
    try {
      const blob = await fetchProposalPdf(p.id);
      const url = URL.createObjectURL(blob);
      if (mode === "view") {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${(p.eventTitle || "SmartBallot-Proposal").replace(/\s+/g, "-")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      toast.error("No stored copy available for this proposal.");
    } finally {
      setBusyId("");
    }
  };

  const handleResend = async (p) => {
    setBusyId(p.id + "resend");
    try {
      const res = await resendProposal(p.id);
      toast.success(`Proposal re-sent to ${res.to}.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend this proposal.");
    } finally {
      setBusyId("");
    }
  };

  const openDoc = async (p) => {
    setBusyId(p.id + "doc");
    try {
      const blob = await fetchProposalDocument(p.id);
      window.open(URL.createObjectURL(blob), "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error("Could not open the governing document.");
    } finally {
      setBusyId("");
    }
  };

  const detail = (Icon, label, value) => (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 text-primary mt-0.5" />
      <span className="text-muted-foreground min-w-[90px]">{label}:</span>
      <span className="font-medium">{value || "—"}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header active="admin" />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <Link to="/admin" data-testid="back-to-admin-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to all leads
        </Link>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground p-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !client ? (
          <Card>
            <CardContent className="p-10 text-center text-muted-foreground">
              <Inbox className="h-8 w-8 mx-auto mb-3 opacity-60" />
              No client found for {email}.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6" data-testid="client-profile">
            <div className="flex items-end justify-between gap-4">
              <div>
                <h1 className="font-display font-800 text-3xl text-foreground tracking-tight" data-testid="client-name">
                  {client.name}
                </h1>
                <p className="text-muted-foreground mt-1">{client.firstNation || client.organization}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Lifetime proposal value</p>
                <p className="font-display font-700 text-2xl text-primary">{formatCurrency(totalValue)}</p>
                <Button
                  size="sm"
                  variant="secondary"
                  data-testid="client-send-email-btn"
                  onClick={() => setEmailOpen(true)}
                  className="gap-2 mt-2"
                >
                  <Mail className="h-3.5 w-3.5" /> Send Email
                </Button>
              </div>
            </div>

            {/* User details */}
            <Card>
              <CardContent className="p-6">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-4">Client details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 text-sm">
                  {detail(Users, "Name", client.name)}
                  {detail(Mail, "Email", client.email)}
                  {detail(Briefcase, "Title", client.jobTitle)}
                  {detail(Building2, "First Nation", client.firstNation || client.organization)}
                  {detail(Phone, "Phone", client.phone)}
                  {detail(
                    MapPin,
                    "Mailing",
                    [client.street, client.city, client.province, client.postalCode].filter(Boolean).join(", ")
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Transaction history */}
            <Card>
              <CardContent className="p-6">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-4">
                  Transaction history ({proposals.length})
                </p>
                <Table data-testid="transaction-history">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Proposal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {proposals.map((p) => (
                      <TableRow key={p.id} data-testid={`transaction-${p.id}`}>
                        <TableCell className="font-medium">
                          {p.eventTitle}
                          {p.governingRegulation && (
                            <span className="block text-xs text-muted-foreground font-normal" data-testid={`tx-reg-${p.id}`}>
                              {GOVERNING_REGULATIONS[p.governingRegulation] || p.governingRegulation}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {ELECTION_TYPES[p.electionType]?.label || p.electionType}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {p.sentAt ? fmtDateTime(p.sentAt) : "Not sent"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{formatCurrency(p.total)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`view-tx-${p.id}`}
                              onClick={() => openPdf(p, "view")}
                              disabled={busyId !== ""}
                              className="gap-1.5 h-8"
                            >
                              {busyId === p.id + "view" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`download-tx-${p.id}`}
                              onClick={() => openPdf(p, "download")}
                              disabled={busyId !== ""}
                              className="gap-1.5 h-8"
                            >
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`resend-tx-${p.id}`}
                              onClick={() => handleResend(p)}
                              disabled={busyId !== ""}
                              className="gap-1.5 h-8"
                            >
                              {busyId === p.id + "resend" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                              Resend
                            </Button>
                            {p.documentKey && (
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`doc-tx-${p.id}`}
                                onClick={() => openDoc(p)}
                                disabled={busyId !== ""}
                                className="gap-1.5 h-8"
                                title="Governing document"
                              >
                                {busyId === p.id + "doc" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <ClientActivityLog proposals={proposals} />
          </div>
        )}
      </main>
      <SendEmailDialog
        proposals={proposals}
        open={emailOpen}
        onOpenChange={setEmailOpen}
        onSent={load}
      />
    </div>
  );
}
